import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, AlertTriangle, Info, CheckCircle, Clock, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../store/authStore';
import { getAlerts, resolveAlert } from '../../api/alerts';
import { getSession } from '../../api/sessions';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { Button } from '../../components/Button';
interface Alert {
  id: string;
  sessionId: string;
  type: string;
  message: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'active' | 'resolved';
  triggeredAt: string;
  resolvedAt: string | null;
}
export function AlertDetail() {
  const {
    alertId
  } = useParams<{
    alertId: string;
  }>();
  const navigate = useNavigate();
  const {
    token
  } = useAuth();
  const [alert, setAlert] = useState<Alert | null>(null);
  const [session, setSession] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [showSuccessAnim, setShowSuccessAnim] = useState(false);
  useEffect(() => {
    const fetchData = async () => {
      if (!token || !alertId) return;
      setIsLoading(true);
      setError(false);
      try {
        // Fetch alerts to find the specific one
        const alertsRes = await getAlerts(token);
        const foundAlert = alertsRes.find((a: Alert) => a.id === alertId);
        if (!foundAlert) {
          throw new Error('Alert not found');
        }
        setAlert(foundAlert);
        // Fetch related session
        const sessionRes = await getSession(token, foundAlert.sessionId);
        setSession(sessionRes);
      } catch (err) {
        // console.error('Failed to fetch alert details', err)
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [token, alertId]);
  const handleResolve = async () => {
    if (!token || !alert) return;
    setIsResolving(true);
    try {
      await resolveAlert(token, alert.id);
      // Show success animation
      setShowSuccessAnim(true);
      // Update local state after a short delay for animation
      setTimeout(() => {
        setAlert({
          ...alert,
          status: 'resolved',
          resolvedAt: new Date().toISOString()
        });
        setShowSuccessAnim(false);
      }, 1500);
    } catch (err) {
      // console.error('Failed to resolve alert', err)
    } finally {
      setIsResolving(false);
    }
  };
  if (isLoading) {
    return <main className="w-full max-w-[800px] mx-auto pb-20 md:pb-0" data-id="element-326">
        <div className="flex items-center mb-8" data-id="element-327">
          <SkeletonBlock width="40px" height="40px" borderRadius="50%" className="mr-4" data-id="element-328" />
          <SkeletonBlock width="150px" height="24px" data-id="element-329" />
        </div>
        <div className="flex flex-col items-center mb-8" data-id="element-330">
          <SkeletonBlock width="80px" height="80px" borderRadius="50%" className="mb-4" data-id="element-331" />
          <SkeletonBlock width="120px" height="20px" className="mb-2" data-id="element-332" />
          <SkeletonBlock width="180px" height="16px" data-id="element-333" />
        </div>
        <div className="space-y-6" data-id="element-334">
          <SkeletonBlock height="200px" borderRadius="20px" data-id="element-335" />
          <SkeletonBlock height="150px" borderRadius="20px" data-id="element-336" />
        </div>
      </main>;
  }
  if (error || !alert) {
    return <main className="w-full max-w-[800px] mx-auto pb-20 md:pb-0" data-id="element-337">
        <div className="flex items-center mb-8" data-id="element-338">
          <button onClick={() => navigate('/app/alerts')} className="w-[40px] h-[40px] rounded-full flex items-center justify-center hover:bg-border/50 transition-colors mr-4" aria-label="Go back" data-id="element-339">
            <ArrowLeft size={24} className="text-text-primary" data-id="element-340" />
          </button>
          <h1 className="text-[20px] font-semibold text-text-primary" data-id="element-341">
            Alert Detail
          </h1>
        </div>
        <div className="bg-danger/10 border border-danger/20 rounded-[16px] p-6 text-center" data-id="element-342">
          <AlertTriangle size={32} className="text-danger mx-auto mb-3" data-id="element-343" />
          <h3 className="text-[16px] font-semibold text-text-primary mb-2" data-id="element-344">
            Failed to load alert
          </h3>
          <p className="text-[14px] text-text-secondary mb-4" data-id="element-345">
            The alert could not be found or there was a connection error.
          </p>
          <Button onClick={() => navigate('/app/alerts')} className="mx-auto" data-id="element-346">
            Back to Alerts
          </Button>
        </div>
      </main>;
  }
  const formatType = (type: string) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  const getSeverityStyles = (severity: string, status: string) => {
    if (status === 'resolved') {
      return {
        bg: 'bg-success',
        color: 'text-success',
        Icon: CheckCircle,
        label: 'Resolved'
      };
    }
    switch (severity) {
      case 'high':
      case 'critical':
        return {
          bg: 'bg-danger',
          color: 'text-danger',
          Icon: AlertCircle,
          label: 'High Severity'
        };
      case 'medium':
        return {
          bg: 'bg-[#F59E0B]',
          color: 'text-[#F59E0B]',
          Icon: AlertTriangle,
          label: 'Medium Severity'
        };
      case 'low':
      default:
        return {
          bg: 'bg-success',
          color: 'text-success',
          Icon: Info,
          label: 'Low Severity'
        };
    }
  };
  const styles = getSeverityStyles(alert.severity, alert.status);
  const Icon = styles.Icon;
  return <main className="w-full max-w-[800px] mx-auto pb-20 md:pb-0 animate-fadeInUp" data-id="element-347">
      {/* Header */}
      <div className="flex items-center justify-between mb-8" data-id="element-348">
        <div className="flex items-center" data-id="element-349">
          <button onClick={() => navigate('/app/alerts')} className="w-[40px] h-[40px] rounded-full flex items-center justify-center hover:bg-border/50 transition-colors mr-2" aria-label="Go back" data-id="element-350">
            <ArrowLeft size={24} className="text-text-primary" data-id="element-351" />
          </button>
          <h1 className="text-[20px] font-semibold text-text-primary" data-id="element-352">
            Alert Detail
          </h1>
        </div>

        {alert.status === 'active' ? <button onClick={handleResolve} disabled={isResolving} className="border border-danger text-danger rounded-pill px-[16px] py-[6px] text-[14px] font-medium hover:bg-danger/5 transition-colors disabled:opacity-50" data-id="element-353">
            {isResolving ? 'Resolving...' : 'Resolve'}
          </button> : <div className="bg-success/20 text-success rounded-pill px-[16px] py-[6px] text-[14px] font-medium" data-id="element-354">
            Resolved
          </div>}
      </div>

      {/* Severity Hero */}
      <div className="flex flex-col items-center mb-10" data-id="element-355">
        <motion.div animate={{
        backgroundColor: alert.status === 'resolved' ? '#00D4D4' : styles.bg.replace('bg-', '')
      }} className={`w-[80px] h-[80px] rounded-full flex items-center justify-center mb-4 ${styles.bg}`} data-id="element-356">
          <Icon size={36} className="text-white" data-id="element-357" />
        </motion.div>
        <h2 className={`text-[18px] font-semibold mb-1 ${styles.color}`} data-id="element-358">
          {styles.label}
        </h2>
        <p className="text-[14px] text-text-secondary" data-id="element-359">
          {formatType(alert.type)} Detected
        </p>
      </div>

      <div className="space-y-6" data-id="element-360">
        {/* Info Card */}
        <div className="bg-white rounded-[20px] shadow-card p-5" data-id="element-361">
          <div className="space-y-4" data-id="element-362">
            <div className="flex justify-between items-center" data-id="element-363">
              <span className="text-[14px] text-text-secondary" data-id="element-364">
                Alert Type
              </span>
              <span className="text-[14px] font-medium text-text-primary" data-id="element-365">
                {formatType(alert.type)}
              </span>
            </div>
            <div className="h-[1px] w-full bg-border" data-id="element-366" />

            <div className="flex justify-between items-center" data-id="element-367">
              <span className="text-[14px] text-text-secondary" data-id="element-368">
                Triggered at
              </span>
              <span className="text-[14px] font-medium text-text-primary text-right max-w-[200px]" data-id="element-369">
                {formatDate(alert.triggeredAt)}
              </span>
            </div>
            <div className="h-[1px] w-full bg-border" data-id="element-370" />

            <div className="flex justify-between items-center" data-id="element-371">
              <span className="text-[14px] text-text-secondary" data-id="element-372">
                Resolved at
              </span>
              <span className={`text-[14px] font-medium text-right max-w-[200px] ${alert.resolvedAt ? 'text-text-primary' : 'text-text-secondary'}`} data-id="element-373">
                {alert.resolvedAt ? formatDate(alert.resolvedAt) : 'Not yet resolved'}
              </span>
            </div>
            <div className="h-[1px] w-full bg-border" data-id="element-374" />

            <div className="flex justify-between items-center" data-id="element-375">
              <span className="text-[14px] text-text-secondary" data-id="element-376">Status</span>
              {alert.status === 'active' ? <div className="px-3 py-1 bg-danger/15 text-danger text-[12px] font-semibold rounded-pill" data-id="element-377">
                  Active
                </div> : <div className="px-3 py-1 bg-success/15 text-success text-[12px] font-semibold rounded-pill" data-id="element-378">
                  Resolved
                </div>}
            </div>
            <div className="h-[1px] w-full bg-border" data-id="element-379" />

            <div className="flex justify-between items-center" data-id="element-380">
              <span className="text-[14px] text-text-secondary" data-id="element-381">Severity</span>
              <div className={`px-3 py-1 text-[12px] font-semibold rounded-pill ${alert.severity === 'high' || alert.severity === 'critical' ? 'bg-danger/15 text-danger' : alert.severity === 'medium' ? 'bg-[#F59E0B]/15 text-[#F59E0B]' : 'bg-success/15 text-success'}`} data-id="element-382">
                {alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}
              </div>
            </div>
          </div>
        </div>

        {/* Message Card */}
        <div className="bg-white rounded-[20px] shadow-card p-5" data-id="element-383">
          <h3 className="text-[16px] font-semibold text-text-primary mb-3" data-id="element-384">
            Alert Message
          </h3>
          <p className="text-[15px] text-text-primary leading-[1.7]" data-id="element-385">
            {alert.message}
          </p>
        </div>

        {/* Related Session Card */}
        {session && <div className="bg-white rounded-[20px] shadow-card p-5" data-id="element-386">
            <h3 className="text-[16px] font-semibold text-text-primary mb-4" data-id="element-387">
              From Session
            </h3>

            <div className="flex items-center gap-4 mb-6" data-id="element-388">
              <div className="w-[48px] h-[48px] bg-primary/10 rounded-full flex items-center justify-center" data-id="element-389">
                <Calendar size={24} className="text-primary" data-id="element-390" />
              </div>
              <div data-id="element-391">
                <p className="text-[15px] font-medium text-text-primary" data-id="element-392">
                  {new Date(session.startedAt).toLocaleDateString('en-US', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
                </p>
                <p className="text-[14px] text-text-secondary mt-1 flex items-center gap-1" data-id="element-393">
                  <Clock size={14} data-id="element-394" />
                  {Math.round((new Date(session.endedAt).getTime() - new Date(session.startedAt).getTime()) / 60000)}{' '}
                  min duration
                </p>
              </div>
            </div>

            <button onClick={() => navigate(`/app/history/${session.id}`)} className="w-full h-[48px] border border-primary text-primary rounded-[14px] font-medium text-[15px] hover:bg-primary/5 transition-colors" data-id="element-395">
              View Session
            </button>
          </div>}

        {/* Resolve Action Section */}
        <AnimatePresence mode="wait" data-id="element-396">
          {alert.status === 'active' && !showSuccessAnim && <motion.div key="resolve-btn" initial={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.9
        }} className="pt-4" data-id="element-397">
              <button onClick={handleResolve} disabled={isResolving} className="w-full h-[52px] bg-danger text-white rounded-[14px] font-medium text-[16px] flex items-center justify-center transition-opacity disabled:opacity-70 mb-3" data-id="element-398">
                {isResolving ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-399" /> : <>
                    <CheckCircle size={20} className="mr-2" data-id="element-400" />
                    Mark as Resolved
                  </>}
              </button>
              <p className="text-[13px] text-text-secondary text-center" data-id="element-401">
                Resolving this alert marks it as reviewed.
              </p>
            </motion.div>}

          {showSuccessAnim && <motion.div key="success-anim" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="pt-4 flex flex-col items-center justify-center py-6" data-id="element-402">
              <div className="relative w-[64px] h-[64px] mb-4" data-id="element-403">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 15
            }} className="absolute inset-0 bg-success rounded-full flex items-center justify-center z-10" data-id="element-404">
                  <CheckCircle size={32} className="text-white" data-id="element-405" />
                </motion.div>
                <motion.div initial={{
              scale: 0.8,
              opacity: 1
            }} animate={{
              scale: 1.5,
              opacity: 0
            }} transition={{
              duration: 1,
              ease: 'easeOut'
            }} className="absolute inset-0 bg-success rounded-full z-0" data-id="element-406" />
              </div>
              <p className="text-[16px] font-medium text-success" data-id="element-407">
                Alert Resolved
              </p>
            </motion.div>}
        </AnimatePresence>
      </div>
    </main>;
}